import {
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
} from '@angular/router';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { filter, shareReplay } from 'rxjs/operators';

@Injectable()
export class HttpLoadingInterceptor implements HttpInterceptor {
  static inFlightRequests = 0;
  static currentToast: Promise<ActiveToast<any>> | undefined = undefined;

  constructor(private toastr: ToastrService) {}

  listenToNavigation(events: Observable<Event>) {
    events.pipe(filter(ev => ev instanceof NavigationStart)).subscribe(() => {
      HttpLoadingInterceptor.inFlightRequests++;
      this.tryShowToast();
    });
    events
      .pipe(
        filter(
          ev =>
            ev instanceof NavigationEnd ||
            ev instanceof NavigationCancel ||
            ev instanceof NavigationError,
        ),
      )
      .subscribe(() => {
        HttpLoadingInterceptor.inFlightRequests--;
        setTimeout(() => this.tryHideToast(), 300);
      });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.tryShowToast();

    /**
     * We need to subscribe to the response in case the calling code
     * unsubscribes before the request completes
     */
    const response$ = next.handle(request).pipe(shareReplay(1));

    response$.subscribe({
      next: (event: HttpEvent<any>) => {
        // if the event is for http request
        if (event instanceof HttpResponse) {
          HttpLoadingInterceptor.inFlightRequests--;

          this.tryHideToast();
        } else {
          if (event.type === HttpEventType.Sent) {
            HttpLoadingInterceptor.inFlightRequests++;

            this.tryShowToast();
          }
        }
      },
      error: () => {
        HttpLoadingInterceptor.inFlightRequests--;

        this.tryHideToast();
      },
    });

    return response$;
  }

  tryShowToast() {
    if (!HttpLoadingInterceptor.currentToast) {
      // Without the setTimeout toastr causes ExpressionChangedAfterItHasBeenCheckedError.
      // ngx-toastr issue: https://github.com/scttcper/ngx-toastr/issues/160
      HttpLoadingInterceptor.currentToast = new Promise(resolve =>
        setTimeout(() => {
          resolve(
            this.toastr.show(
              '<div class="progress"><div class="progress-bar progress-bar-striped progress-bar-animated w-100" role="progressbar"><span class="visually-hidden">Loading&hellip;</span></div></div>',
              '',
              {
                closeButton: false,
                enableHtml: true,
                toastClass: 'toast-loading',
                tapToDismiss: false,
                disableTimeOut: true,
                onActivateTick: true,
                easeTime: 150,
              },
            ),
          );
        }),
      );
    }
  }

  tryHideToast() {
    if (HttpLoadingInterceptor.currentToast && HttpLoadingInterceptor.inFlightRequests <= 0) {
      setTimeout(() => {
        if (HttpLoadingInterceptor.currentToast && HttpLoadingInterceptor.inFlightRequests <= 0) {
          HttpLoadingInterceptor.inFlightRequests = 0;
          HttpLoadingInterceptor.currentToast.then(({ toastId }) => this.toastr.remove(toastId));
          HttpLoadingInterceptor.currentToast = undefined;
        }
      }, 100);
    }
  }
}
